"use client"

import { useEffect, useState } from "react"
import { usePathname } from "next/navigation"
import { useTranslations } from "next-intl"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/lib/auth-store"
import { useModalContent } from "@/lib/modal-content"
import { useModalSlot } from "@/lib/client/modal-coordinator"

const COOKIE_NAME = "goonru_age_verified"
const COOKIE_DAYS = 365

function getAgeCookie(): boolean {
  if (typeof document === "undefined") return false
  return document.cookie.split(";").some((c) => c.trim().startsWith(`${COOKIE_NAME}=true`))
}

function setAgeCookie() {
  const expires = new Date()
  expires.setDate(expires.getDate() + COOKIE_DAYS)
  document.cookie = `${COOKIE_NAME}=true; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
  // keep localStorage in sync for backwards compat
  try { localStorage.setItem("goonru_age_verified", "true") } catch {}
}

export function AgeGate({ children, previewMode, onDismiss }: { children: React.ReactNode; previewMode?: boolean; onDismiss?: () => void }) {
  const t = useTranslations("ageGate")
  const [verified, setVerified] = useState<boolean | null>(null)
  const { user, isLoading } = useAuthStore()
  const pathname = usePathname()
  // The widget/tracking-link routes are meant to be embedded on third-party sites or shared as a
  // bare link to a stranger with zero Goonru context — the full branded, blurred interstitial
  // feels out of place there. Same verification (same cookie, same legal confirmation), just a
  // faster, lighter inline version instead of the full-page takeover used elsewhere.
  const isEmbedRoute = pathname?.startsWith("/widget/") || pathname?.startsWith("/w/")
  // The legal pages (privacy, terms, ...) and the "too young" exit page hold no adult
  // content and have to be readable without clicking through the gate: the gate links to
  // them, and Google's app verification (and any crawler) must be able to read the privacy
  // policy. Before this, the gate replaced the whole page for unverified visitors, so these
  // URLs showed nothing but the gate.
  const isPublicRoute = pathname === "/legal" || pathname?.startsWith("/legal/") || pathname === "/too-young"
  // Admin-editable art (lib/modal-content-defs.ts), art only. Only fetched
  // when the gate is actually being shown, never for verified visitors.
  const content = useModalContent("age_gate", (verified === false && !isPublicRoute) || !!previewMode)
  // While the gate is up, hold the single popup slot so nothing else opens
  // underneath it. The gate itself always renders (it is the top priority).
  useModalSlot("ageGate", verified === false && !isPublicRoute && !(!isLoading && user), { bypass: !!previewMode })

  useEffect(() => {
    // Cookie is the source of truth — resilient to localStorage clears during payment redirects
    const cookieOk = getAgeCookie()
    if (cookieOk) {
      // Back-fill localStorage so old checks still pass
      try { localStorage.setItem("goonru_age_verified", "true") } catch {}
      setVerified(true)
      return
    }
    // Fallback: check localStorage (covers existing users before this cookie was added)
    try {
      const stored = window.localStorage?.getItem("goonru_age_verified")
      if (stored === "true") {
        setAgeCookie()
        setVerified(true)
        return
      }
    } catch {}
    setVerified(false)
  }, [])

  const handleConfirm = () => {
    if (previewMode) { onDismiss?.(); return }
    setAgeCookie()
    setVerified(true)
  }

  const handleExit = () => {
    if (previewMode) { onDismiss?.(); return }
    window.location.href = "/too-young"
  }

  if (isPublicRoute && !previewMode) return <>{children}</>

  // Still loading from storage
  if (verified === null && !previewMode) return null

  // Authenticated users are verified adults by definition — skip the gate
  if (!previewMode && (verified || (!isLoading && user))) {
    if (user && !getAgeCookie()) setAgeCookie()
    return <>{children}</>
  }

  if (isEmbedRoute && !previewMode) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/90 p-4">
        <div className="flex items-center gap-3 max-w-sm w-full p-4 rounded-xl border border-border/50 bg-card shadow-xl">
          <span className="text-2xl select-none shrink-0">🔞</span>
          <p className="text-xs text-muted-foreground flex-1">
            {t("embedPrompt")}
          </p>
          <Button size="sm" className="shrink-0" onClick={handleConfirm}>{t("embedConfirm")}</Button>
        </div>
      </div>
    )
  }

  // The 1.5s cap inside useModalContent means a slow read can never keep the
  // gate from showing: it falls back to the built-in art.
  if (!content.ready) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-xl" />

      <div className="relative z-10 flex w-full max-w-[440px] flex-col items-center gap-3">
      <div className="w-full rounded-2xl overflow-hidden shadow-2xl border border-white/[0.06]" style={{ zoom: "min(1, calc((95svh - 84px) / 700px))" }}>
        {/* Single container — art fills it, content overlays the dark bottom zone */}
        <div className="relative w-full" style={{ aspectRatio: "438 / 625" }}>
          {/* Background scene — stretched taller than its natural shape so the bottom
              text (which wraps to two lines on phones) has room above the border. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={content.art("background")}
            alt=""
            className="absolute inset-0 h-full w-full object-fill"
            draggable={false}
          />
          {/* Goop GIF — nudged down a bit, head near top of frame */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={content.art("character")}
            alt=""
            className="absolute top-[4%] left-1/2 -translate-x-1/2 w-[76%] object-contain"
            draggable={false}
          />

          {/* Content overlaid — pushed up to give room for all elements */}
          <div className="absolute bottom-0 left-0 right-0 z-10 flex flex-col justify-end gap-2 px-8 pb-6">
            <div className="text-center space-y-1.5">
              <p className="font-bold text-2xl text-white drop-shadow-sm">{t("title")}</p>
              <p className="text-[15px] text-white/60 leading-relaxed">
                {t("description")}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Button size="lg" className="w-full font-semibold text-base" onClick={handleConfirm}>
                {t("confirm")}
              </Button>
              <Button size="lg" variant="outline" className="w-full text-base bg-white/5 border-white/15 text-white hover:bg-white/10" onClick={handleExit}>
                {t("exit")}
              </Button>
            </div>

            <p className="text-xs text-white/30 text-center">
              {t("legalNote")}
            </p>
          </div>
        </div>
      </div>
      {/* What the site is, plus the policy links Google's app verification looks for.
          Outside the card so it never gets clipped by the art's fixed height (phones). */}
      <div className="flex flex-col items-center gap-1.5 px-2 text-center">
        <p className="text-[11px] leading-snug text-white/55">{t("about")}</p>
        <p className="text-[10px] leading-snug text-white/45">{t("aiNote")}</p>
        <p className="text-xs">
          <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-white/65 underline underline-offset-2 hover:text-white/90">{t("privacyLink")}</a>
          <span className="text-white/25 mx-2" aria-hidden="true">·</span>
          <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="text-white/65 underline underline-offset-2 hover:text-white/90">{t("termsLink")}</a>
        </p>
      </div>
      </div>
    </div>
  )
}
